import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { StatBlockProps } from '../../../types/composer';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import RichText from '../block-ui/RichText';
import titleSchema from '../../../lib/composer/schemas/title';
import subtitleSchema from '../../../lib/composer/schemas/subtitle';
import textSchema from '../../../lib/composer/schemas/text';
import BlockWrapper from '../BlockWrapper';
import StatItems from './StatItem';
import statsSchema from '../../../lib/composer/schemas/stat';
import themeSchema, {
  themeToTailwindClassMap,
} from '../../../lib/composer/schemas/theme';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

/**
 * StatBlock component.
 *
 * Renders a statistics block with an optional title, subtitle, rich text, and a list of stat items.
 * Used for displaying grouped statistics in a visually organized block.
 * @component
 * @param {StatBlockProps} props - The props for the StatBlock component.
 * @param {string} [props.title] - The main heading for the stat block.
 * @param {string} [props.subTitle] - The subheading for the stat block.
 * @param {string} [props.text] - Optional rich text content to display below the headings.
 * @param {boolean} [props.topHeader] - If true, renders the subtitle as a top-level header.
 * @param {Array<StatItemProps>} [props.stats] - An array of stat items to display.
 * @returns {JSX.Element} The rendered StatBlock component.
 */
const StatBlock = (props: StatBlockProps) => {
  const { title, subtitle, text, theme = 'transparent', stats } = props;
  const bgTheme = themeToTailwindClassMap[theme];

  return (
    <Flex className={cn(bgTheme)}>
      <BlockWrapper>
        <Flex className="flex-col gap-3 items-center text-center">
          {title && <Heading text={title} theme={theme} />}
          {subtitle && <Subheading text={subtitle} theme={theme} />}
          {text && <RichText html={text} theme={theme} />}
        </Flex>
        {stats && <StatItems stats={stats} theme={theme} />}
      </BlockWrapper>
    </Flex>
  );
};

StatBlock.schema = {
  name: 'stat-block',
  label: 'Stat Block',
  editableProps: [
    titleSchema,
    subtitleSchema,
    textSchema,
    statsSchema,
    themeSchema,
  ],
};

export default StatBlock;
